import {
  BarChart3,
  BookOpen,
  Bot,
  ClipboardList,
  Eye,
  FileSearch,
  FileStack,
  Flame,
  Gauge,
  GitCompare,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  MessageSquareText,
  Newspaper,
  PackageCheck,
  Plug,
  Search,
  SearchCheck,
  Settings,
  ShieldUser,
  Sparkles,
  Target,
  Telescope,
  TrendingUp,
  Wand2,
} from 'lucide-react'
import type { NavItem, NavItemGroup } from './routing.types'

/**
 * Main navigation items for the application sidebar.
 *
 * Defines the primary navigation groups and their associated items that appear
 * in the main section of the sidebar. Each group contains a label and an array
 * of navigation items with their routes, labels, and icons.
 */
export const NAV_ITEMS: readonly NavItemGroup[] = [
  {
    labelKey: 'sidebarNav.overview',
    navItems: [
      {
        to: '/',
        labelKey: 'sidebarNav.overviewSummary',
        icon: LayoutDashboard,
      },
      {
        to: '/seo/details',
        labelKey: 'sidebarNav.seoDetails',
        icon: SearchCheck,
      },
      {
        to: '/overview/ai-traffic',
        labelKey: 'sidebarNav.overviewAiTraffic',
        icon: Gauge,
      },
      {
        to: '/overview/deliverables',
        labelKey: 'sidebarNav.overviewDeliverables',
        icon: PackageCheck,
      },
    ],
  },
  {
    labelKey: 'sidebarNav.data',
    navItems: [
      {
        to: '/data/dashboard',
        labelKey: 'sidebarNav.dataDashboard',
        icon: Eye,
      },
      {
        labelKey: 'sidebarNav.dataPrompts',
        icon: MessageSquareText,
        subItems: [
          {
            to: '/data/prompts',
            labelKey: 'sidebarNav.dataPromptsList',
          },
          {
            to: '/data/prompts/responses',
            labelKey: 'sidebarNav.dataResponses',
          },
        ],
      },
      {
        to: '/data/sources',
        labelKey: 'sidebarNav.dataSources',
        icon: FileStack,
      },
      {
        to: '/data/actions',
        labelKey: 'sidebarNav.dataActions',
        icon: Target,
      },
    ],
  },
  {
    labelKey: 'sidebarNav.competitiveAnalysis',
    navItems: [
      {
        to: '/keyword-gaps',
        labelKey: 'sidebarNav.keywordGap',
        icon: GitCompare,
      },
      {
        to: '/keyword-positioning',
        labelKey: 'sidebarNav.keywordPositioning',
        icon: TrendingUp,
      },
    ],
  },
  {
    labelKey: 'sidebarNav.content',
    navItems: [
      {
        to: '/contents',
        labelKey: 'sidebarNav.myContents',
        icon: Newspaper,
      },
      {
        to: '/native-content',
        labelKey: 'sidebarNav.nativeContent',
        icon: Wand2,
      },
      {
        to: '/content/bulk',
        labelKey: 'sidebarNav.bulkGeneration',
        icon: Layers,
      },
      {
        to: '/content-plans',
        labelKey: 'sidebarNav.contentPlan',
        icon: ClipboardList,
      },
      {
        to: '/keywords',
        labelKey: 'sidebarNav.keywordResearch',
        icon: Search,
      },
      {
        to: '/content-optimization',
        labelKey: 'sidebarNav.contentOptimization',
        icon: Sparkles,
      },
      {
        to: '/landing-pages',
        labelKey: 'sidebarNav.landingPages',
        icon: LayoutTemplate,
      },
      {
        to: '/social-analytics',
        labelKey: 'sidebarNav.socialAnalytics',
        icon: BarChart3,
      },
    ],
  },
  {
    labelKey: 'sidebarNav.research',
    navItems: [
      {
        to: '/seo-audit',
        labelKey: 'sidebarNav.seoAudit',
        icon: FileSearch,
        meta: { require: { superAdminOnly: true } },
      },
      {
        to: '/prospect-audits',
        labelKey: 'sidebarNav.prospectAudits',
        icon: Telescope,
        meta: { require: { superAdminOnly: true } },
      },
    ],
  },
  {
    labelKey: 'sidebarNav.tools',
    navItems: [
      {
        to: '/agent',
        labelKey: 'sidebarNav.sergeo',
        icon: Bot,
      },
      {
        to: '/trend-filter',
        labelKey: 'sidebarNav.trendFilter',
        icon: Flame,
      },
      {
        to: '/integrations',
        labelKey: 'sidebarNav.integrations',
        icon: Plug,
      },
      {
        to: '/knowledge-base',
        labelKey: 'sidebarNav.knowledgeBase',
        icon: BookOpen,
      },
    ],
  },
]

/**
 * Footer navigation items for the application sidebar.
 *
 * Defines the navigation items that appear in the footer section of the sidebar,
 * typically containing utility links like support and settings. These items are
 * usually placed at the bottom of the sidebar for easy access.
 *
 * Each NavItem accepts NavLink props, allowing customization of classes, events,
 * and other component-specific properties.
 */
export const FOOTER_ITEMS: NavItem[] = [
  {
    to: '/company-settings',
    labelKey: 'sidebarNav.companySettings',
    icon: Settings,
  },
]

/**
 * Profile dropdown navigation items for the application sidebar.
 *
 * Defines the navigation items that appear in the user profile dropdown menu,
 * typically containing user-specific actions like profile settings, preferences,
 * or account management options.
 *
 * Each NavItem accepts NavLink props, allowing customization of classes, events,
 * and other component-specific properties.
 */
export const PROFILE_DROPDOWN_ITEMS: NavItem[] = [
  {
    to: '/super-admin',
    labelKey: 'sidebarNav.superAdmin',
    icon: ShieldUser,
    meta: { require: { superAdminOnly: true } },
  },
  {
    to: '/super-admin/deliverables',
    labelKey: 'sidebarNav.superAdminDeliverables',
    icon: PackageCheck,
    meta: { require: { superAdminOnly: true } },
  },
]
